import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { SubscriptionPlan } from "../../domain/entities/SubscriptionPlan";
import { onCancelSubscriptionWithPaypal, onCreateSubscriptionWithPaypal, onGetSubscriptionAll, onGetSubscriptionByUser } from "../store/SubscriptionsThunk";
import { useAlert } from "../../../../core/hooks/useAlert";

const isPaypalCheckoutUrl = (value: unknown): value is string => {
    if (typeof value !== "string") return false;
    try {
        const url = new URL(value);
        return url.protocol === "https:" &&
            (url.hostname === "paypal.com" || url.hostname.endsWith(".paypal.com"));
    } catch {
        return false;
    }
};

export const useSubscription = () => {
    const dispatch: AppDispatch = useDispatch();
    const { showAlert } = useAlert();
    const {
        subscriptionsAll,
        subscriptionsByUser,
        isLoading: isLoadingSubscriptionsAll,
        isLoadingSubscriptionByUser,
        hasLoadedSubscriptionsAll,
        hasLoadedSubscriptionByUser,
    } = useSelector((state: RootState) => state.subscriptions);
    
    const [selectedValue, setSelectedValue] = useState<SubscriptionPlan | undefined>(
        subscriptionsByUser 
            ? subscriptionsAll?.find(plan => plan.id === subscriptionsByUser.subscription_id) 
            : undefined
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isSubscriptionPlan, setIsSubscriptionPlan] = useState(false);
    const [isOpenWarning, setIsOpenWarning] = useState(false);
    const paymentInProgress = useRef(false);

    useEffect(() => {
        if (!hasLoadedSubscriptionsAll && !isLoadingSubscriptionsAll) {
            void dispatch(onGetSubscriptionAll());
        }
    }, [dispatch, hasLoadedSubscriptionsAll, isLoadingSubscriptionsAll]);

    useEffect(() => {
        if (!hasLoadedSubscriptionByUser && !isLoadingSubscriptionByUser) {
            void dispatch(onGetSubscriptionByUser());
        }
    }, [dispatch, hasLoadedSubscriptionByUser, isLoadingSubscriptionByUser]);

    const handleChange = (value: SubscriptionPlan | undefined) => {
        setSelectedValue(value);
    };

    const handleAcceptSubscription = async () => {
        if (!selectedValue || paymentInProgress.current) return;
        paymentInProgress.current = true;
        let paypalWindow: Window | null = null;
        setIsLoading(true);
        try {
            paypalWindow = window.open("", "_blank");
            const result = await dispatch(onCreateSubscriptionWithPaypal({ subscription_id: selectedValue.id }));
            const url = result.payload;
            if (!isPaypalCheckoutUrl(url)) throw new Error("Checkout unavailable");
            if (paypalWindow) {
                paypalWindow.opener = null;
                paypalWindow.location.href = url;
            } else {
                window.open(url, "_self");
            }
        } catch {
            paypalWindow?.close();
            showAlert({
                message: "PayPal checkout could not be opened. Please try again or contact Unjabbed Support.",
                type: "error",
            });
        } finally {
            paymentInProgress.current = false;
            setIsLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (paymentInProgress.current) return;
        paymentInProgress.current = true;
        setIsSubscriptionPlan(true);
        setIsOpenWarning(false);
        setIsLoading(true);
        try {
            const cancellation = await dispatch(onCancelSubscriptionWithPaypal());
            if (cancellation.meta.requestStatus !== "fulfilled") throw new Error("Cancellation unavailable");
            const saved = await dispatch(onGetSubscriptionByUser());
            if (saved.meta.requestStatus !== "fulfilled") {
                showAlert({
                    message: "Your cancellation was sent, but we could not refresh your subscription. Please check again or contact Unjabbed Support.",
                    type: "error",
                });
            }
        } catch {
            showAlert({
                message: "We could not confirm your cancellation. Please check your subscription before trying again or contact Unjabbed Support.",
                type: "error",
            });
        } finally {
            paymentInProgress.current = false;
            setIsLoading(false);
            setIsSubscriptionPlan(false);
        }
    };

    return {
        subscriptionsAll,
        subscriptionsByUser,
        isLoadingSubscriptionByUser,
        selectedValue,
        isLoading,
        isSubscriptionPlan,
        isOpenWarning,
        handleChange,
        handleAcceptSubscription,
        handleCancelSubscription,
        setIsSubscriptionPlan,
        setIsOpenWarning,
    };
};
